import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertCircle, CalendarIcon, Users, Edit } from "lucide-react";
import { ImpactedReservation } from "@/lib/availabilityImpactUtils";

interface AvailabilityImpactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableName: string;
  impactedReservations: ImpactedReservation[];
  onConfirm: () => void;
  onEditReservation: (reservationId: string) => void;
}

export function AvailabilityImpactDialog({
  open,
  onOpenChange,
  tableName,
  impactedReservations,
  onConfirm,
  onEditReservation,
}: AvailabilityImpactDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const getBadgeVariant = (reason: 'weekday' | 'specific_date') => {
    return reason === 'specific_date' ? 'destructive' : 'secondary';
  };

  const getBadgeText = (reason: 'weekday' | 'specific_date') => {
    return reason === 'specific_date' ? '🚫 Date bloquée' : '🗓️ Jour non disponible';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-warning" />
            Impact sur les Réservations Existantes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Cette modification de disponibilité <strong>n'affectera PAS</strong> les réservations 
              existantes listées ci-dessous.
              <br /><br />
              Cependant, ces réservations sont actuellement assignées à{" "}
              <strong>{tableName}</strong> sur des dates qui ne seront plus disponibles pour 
              de <strong>NOUVELLES</strong> réservations.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold">
              📋 {impactedReservations.length} réservation{impactedReservations.length > 1 ? 's' : ''} concernée{impactedReservations.length > 1 ? 's' : ''} :
            </h4>

            <ScrollArea className={impactedReservations.length > 5 ? "h-[300px]" : ""}>
              <div className="space-y-2">
                {impactedReservations.map((reservation) => (
                  <Card key={reservation.id} className="border-muted">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1 flex-1">
                            <p className="font-medium">🍽️ {reservation.customerName}</p>
                            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3 w-3" />
                                <span>{reservation.date} à {reservation.startTime}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="h-3 w-3" />
                                <span>{reservation.numberOfPeople} personne{reservation.numberOfPeople > 1 ? 's' : ''}</span>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onEditReservation(reservation.id)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Modifier
                          </Button>
                        </div>
                        <Badge variant={getBadgeVariant(reservation.impactReason)}>
                          {getBadgeText(reservation.impactReason)}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Annuler
          </Button>
          <Button onClick={handleConfirm}>
            Continuer quand même
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
